export const RECAPTCHA_ACTIONS = {
  LOGIN: 'LOGIN',
  SIGNUP: 'SIGNUP',
  CHECKOUT: 'CHECKOUT',
}

export const RECAPTCHA_ERROR = 'Captcha verification failed. Please try again.'

const PROTECTION_SETTING_BY_ACTION = {
  [RECAPTCHA_ACTIONS.LOGIN]: 'protectLogin',
  [RECAPTCHA_ACTIONS.SIGNUP]: 'protectSignup',
  [RECAPTCHA_ACTIONS.CHECKOUT]: 'protectCheckout',
}

const scriptLoads = new Map()

export const getRecaptchaConfig = shop => {
  return (shop && shop.recaptcha) || { enabled: false }
}

export const isRecaptchaProtected = (shop, action) => {
  const config = getRecaptchaConfig(shop)
  const setting = PROTECTION_SETTING_BY_ACTION[action]

  return !!setting && config.enabled === true && config[setting] !== false
}

const getEnterpriseApi = () => {
  return typeof window !== 'undefined' && window.grecaptcha && window.grecaptcha.enterprise
}

const waitUntilReady = enterprise => {
  return new Promise((resolve, reject) => {
    if (!enterprise || typeof enterprise.ready !== 'function') {
      reject(new Error('reCAPTCHA Enterprise API is unavailable'))
      return
    }

    try {
      enterprise.ready(resolve)
    } catch (error) {
      reject(error)
    }
  })
}

const loadRecaptcha = siteKey => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.reject(new Error('reCAPTCHA requires a browser'))
  }

  const enterprise = getEnterpriseApi()
  if (enterprise) return waitUntilReady(enterprise).then(() => enterprise)

  if (scriptLoads.has(siteKey)) return scriptLoads.get(siteKey)

  const scriptPromise = new Promise((resolve, reject) => {
    const existingScript = Array.from(document.querySelectorAll('script[data-eontyre-recaptcha-key]'))
      .find(script => script.dataset.eontyreRecaptchaKey === siteKey)
    const script = existingScript || document.createElement('script')
    const handleLoad = () => {
      const loadedEnterprise = getEnterpriseApi()
      waitUntilReady(loadedEnterprise)
        .then(() => resolve(loadedEnterprise))
        .catch(error => {
          scriptLoads.delete(siteKey)
          reject(error)
        })
    }
    const handleError = () => {
      scriptLoads.delete(siteKey)
      reject(new Error('Could not load reCAPTCHA'))
    }

    script.addEventListener('load', handleLoad, { once: true })
    script.addEventListener('error', handleError, { once: true })
    if (existingScript) return

    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${encodeURIComponent(siteKey)}`
    script.async = true
    script.dataset.eontyreRecaptchaKey = siteKey
    document.head.appendChild(script)
  })

  scriptLoads.set(siteKey, scriptPromise)
  return scriptPromise
}

export const executeRecaptcha = (shop, action) => {
  if (!isRecaptchaProtected(shop, action)) {
    return Promise.resolve({})
  }

  const siteKey = String(getRecaptchaConfig(shop).siteKey || '').trim()
  if (!siteKey) {
    return Promise.reject(new Error('Missing reCAPTCHA site key'))
  }

  return loadRecaptcha(siteKey)
    .then(enterprise => enterprise.execute(siteKey, { action }))
    .then(token => {
      if (!token) {
        throw new Error('reCAPTCHA did not return a token')
      }

      return {
        reCaptchaToken: token,
        reCaptchaAction: action,
        reCaptchaSiteKey: siteKey,
      }
    })
}
